import { forwardRef, useMemo } from 'react';
import {
  BackSide,
  ColorRepresentation,
  Group,
  Matrix4,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from 'three';

import { Line } from '@react-three/drei';
import { GroupProps } from '@react-three/fiber';
import { toVector2 } from '@spazious/dollhouse-three';

export interface PolygonProps extends GroupProps {
  points: ([number, number] | Vector2)[];
  color?: ColorRepresentation;
  dashed?: boolean;
  lineWidth?: number;
  backgroundOpacity?: number;
}

export const Polygon = forwardRef<Group, PolygonProps>(
  (
    { points, color = 'black', dashed, lineWidth = 1, backgroundOpacity = 0.1, ...restProps },
    ref
  ) => {
    const up = useMemo(() => new Vector3(0, 1, 0), []);

    const mt = useMemo(() => new Matrix4().lookAt(new Vector3(0, 0, 0), up, up), [up]);
    const v3Points = useMemo(
      () =>
        points.map((point) =>
          new Vector3().fromArray([...toVector2(point).toArray(), 0]).applyMatrix4(mt)
        ),
      [mt, points]
    );

    const rectGeometry = useMemo(
      () =>
        new ShapeGeometry(new Shape().setFromPoints(points.map((v) => toVector2(v)))).applyMatrix4(
          mt
        ),
      [mt, points]
    );

    return (
      <group ref={ref} {...restProps}>
        <mesh geometry={rectGeometry} name="polygon">
          <meshBasicMaterial
            polygonOffset
            transparent
            color={color}
            opacity={backgroundOpacity}
            polygonOffsetUnits={-10}
            side={BackSide}
          />
        </mesh>

        <Line
          polygonOffset
          color={color}
          dashed={dashed}
          depthTest={false}
          lineWidth={lineWidth}
          points={v3Points}
          polygonOffsetUnits={-20}
          renderOrder={1004}
        />
      </group>
    );
  }
);
